/**
 * useScheduleAwareRecommendations
 * 
 * Phase 4: Schedule-aware training recommendations for Learning Center.
 * Aggregates ticket context from technician's schedule window (±5 days)
 * and scores artifacts based on upcoming work.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
// DTC-J: route the schedule-window "now" anchor + future-offering
// query bound through the canonical clock so recommendation scoring
// is deterministic under `clock.setFixed(...)`. The class-D-by-design
// browser-local fallback at L125-129 (when no TZ supplied) is
// preserved with explicit comment.
import { clock } from '@/utils/clock';
import { addDays, subDays, startOfDay, isToday, isTomorrow, format } from 'date-fns';
import { 
  scoreArtifactsForTicket, 
  extractDTCs, 
  type TicketContext, 
  type ScoredContextualArtifact,
  type FitmentInfo,
  type AdjacencyInfo
} from '@/utils/contextualTrainingScorer';
import { buildTicketTrainingContextBatch } from '@/utils/buildTicketTrainingContext';
import { getStyleAffinityBoost } from '@/utils/divinationEngine';
// BAT-A1: bound `.in('artifact_id', artifactIds)` and `.in('source_job_id',
// uniqueJobIds)` clause sizes. The fitment read fans out across every
// active training_artifact (typically 100–500, occasionally more after
// content imports); the adjacency read fans out across the union of
// distinct ticket_jobs.job_id values across the schedule window
// (bounded by the shop's catalog, often 50–200, but legitimately can spike
// after large parts/job catalog imports). Routing both through
// `chunkInQuery` makes their cost shape predictable instead of relying on
// callsite-implicit bounds that drift over time.
import { chunkInQuery } from '@/utils/chunkInQuery';
import type { TrainingArtifact, LearningStyle } from '@/types/training';

// =============================================
// TYPES
// =============================================

export interface ScheduleContext {
  ticketCount: number;
  jobCategories: string[];
  fuelTypes: string[];
  allDTCs: string[];
  allKeywords: string[];
  dateRange: {
    start: string;
    end: string;
  };
}

export interface CategorizedRecommendations {
  helpNow: ScoredContextualArtifact[];
  prepUpcoming: ScoredContextualArtifact[];
  skillGrowth: ScoredContextualArtifact[];
}

interface ScheduleTicket {
  id: string;
  scheduled_date: string;
  vehicle?: {
    engine_size?: string | null;
    make?: string | null;
    model?: string | null;
    year?: number | null;
  } | null;
  ticket_jobs?: Array<{
    notes?: string | null;
    job?: {
      id?: string | null;
      name?: string | null;
      category?: string | null;
    } | null;
  }> | null;
}

interface UseScheduleAwareRecommendationsOptions {
  technicianProfileId: string;
  windowDays?: number;
  enabled?: boolean;
  /**
   * IANA timezone of the viewing tech's shop. When provided, the "today" /
   * "tomorrow" / "upcoming" / "past" urgency classification compares shop-
   * local day labels rather than browser-local dates — so advisors or techs
   * viewing from a different TZ than the shop still see the correct bucket.
   */
  shopTimezone?: string;
}

// =============================================
// HELPER FUNCTIONS
// =============================================

/**
 * Infer fuel type from engine size
 */
function inferFuelType(engineSize: string | null | undefined): string | null {
  if (!engineSize) return null;
  const lower = engineSize.toLowerCase();
  if (lower.includes('electric') || lower.includes('ev') || lower.includes('battery')) return 'ev';
  if (lower.includes('hybrid')) return 'hybrid';
  if (lower.includes('diesel')) return 'diesel';
  if (lower.includes('gas') || /\d+\.\d+l/i.test(lower)) return 'gas';
  return null;
}

/**
 * Determine urgency category for a ticket.
 * When `shopTimezone` is supplied, comparison is done via shop-local YYYY-MM-DD
 * strings so cross-TZ viewers still see the correct bucket.
 */
function getUrgency(
  scheduledDate: string,
  shopTimezone?: string,
): 'now' | 'tomorrow' | 'upcoming' | 'past' {
  if (shopTimezone) {
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-CA', {
        timeZone: shopTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    // scheduled_date is stored shop-local as YYYY-MM-DD; if a full ISO was
    // passed (legacy), reformat it in shop TZ first.
    const schedLabel = scheduledDate.length === 10 && scheduledDate[4] === '-'
      ? scheduledDate
      : fmt(new Date(scheduledDate));
    const now = clock.now();
    const todayLabel = fmt(now);
    const tomorrowLabel = fmt(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    if (schedLabel === todayLabel) return 'now';
    if (schedLabel === tomorrowLabel) return 'tomorrow';
    return schedLabel < todayLabel ? 'past' : 'upcoming';
  }
  // Fallback — browser-local (matches historical behavior if no TZ supplied).
  const date = new Date(scheduledDate);
  if (isToday(date)) return 'now';
  if (isTomorrow(date)) return 'tomorrow';
  const today = startOfDay(clock.now());
  if (date < today) return 'past';
  return 'upcoming';
}

// =============================================
// MAIN HOOK
// =============================================

export function useScheduleAwareRecommendations({
  technicianProfileId,
  windowDays = 5,
  enabled = true,
  shopTimezone,
}: UseScheduleAwareRecommendationsOptions) {
  return useQuery({
    queryKey: ['schedule-aware-recommendations', technicianProfileId, windowDays, shopTimezone ?? ''],
    queryFn: async (): Promise<{
      recommendations: CategorizedRecommendations;
      scheduleContext: ScheduleContext;
      isLoading: boolean;
    }> => {
      // Step 1: Find shop_technician_id from employment history
      const { data: employment, error: empError } = await supabase
        .from('technician_employment_history')
        .select('shop_technician_id, shop_id')
        .eq('technician_profile_id', technicianProfileId)
        .is('end_date', null)
        .single();

      if (empError || !employment?.shop_technician_id) {
        console.warn('No active employment found for technician profile:', technicianProfileId);
        return {
          recommendations: { helpNow: [], prepUpcoming: [], skillGrowth: [] },
          scheduleContext: {
            ticketCount: 0,
            jobCategories: [],
            fuelTypes: [],
            allDTCs: [],
            allKeywords: [],
            dateRange: { start: '', end: '' },
          },
          isLoading: false,
        };
      }

      // Step 2: Query tickets in schedule window
      const today = startOfDay(clock.now());
      const startDate = subDays(today, windowDays);
      const endDate = addDays(today, windowDays);

      const { data: tickets, error: ticketsError } = await supabase
        .from('tickets')
        .select(`
          id,
          scheduled_date,
          vehicle:vehicles(engine_size, make, model, year),
          ticket_jobs(
            notes,
            job:jobs(id, name, category)
          )
        `)
        .eq('assigned_tech_id', employment.shop_technician_id)
        .gte('scheduled_date', format(startDate, 'yyyy-MM-dd'))
        .lte('scheduled_date', format(endDate, 'yyyy-MM-dd'))
        // DMC-D3b: canonical active-ticket filter — both reps excluded.
        // The status-side IN excludes Deleted/Completed; the
        // .is('deleted_at', null) is defense-in-depth atop the DMC-D1
        // primitive fix so any historical ghost row cannot pollute the
        // schedule-aware recommendation set.
        .not('status', 'in', '("Deleted","Completed")')
        .is('deleted_at', null);

      if (ticketsError) {
        console.error('Failed to fetch schedule tickets:', ticketsError);
        throw ticketsError;
      }

      const scheduleTickets = (tickets || []) as ScheduleTicket[];

      // Batch-fetch workflow context for all tickets (5 queries total + optional 6th)
      const allTicketIds = scheduleTickets.map(t => t.id);
      const workflowContextMap = await buildTicketTrainingContextBatch(allTicketIds);

      // Step 3: Aggregate schedule context
      const jobCategories = new Set<string>();
      const fuelTypes = new Set<string>();
      const allDTCs: string[] = [];
      const allJobTitles: string[] = [];
      const allWorkflowText: string[] = [];
      const allJobIds: string[] = [];

      // Group tickets by urgency
      const ticketsByUrgency: Record<'now' | 'tomorrow' | 'upcoming', ScheduleTicket[]> = {
        now: [],
        tomorrow: [],
        upcoming: [],
      };

      for (const ticket of scheduleTickets) {
        const urgency = getUrgency(ticket.scheduled_date, shopTimezone);
        if (urgency !== 'past') {
          ticketsByUrgency[urgency].push(ticket);
        }

        // Aggregate fuel type
        const fuelType = inferFuelType(ticket.vehicle?.engine_size);
        if (fuelType) fuelTypes.add(fuelType);

        // Aggregate jobs (titles, categories, IDs)
        for (const tj of ticket.ticket_jobs || []) {
          if (tj.job?.category) jobCategories.add(tj.job.category);
          if (tj.job?.name) allJobTitles.push(tj.job.name);
          if (tj.job?.id) allJobIds.push(tj.job.id);
        }

        // Aggregate workflow context (text + DTCs)
        const wfCtx = workflowContextMap.get(ticket.id);
        if (wfCtx) {
          allWorkflowText.push(wfCtx.text);
          allDTCs.push(...wfCtx.dtcs);
        }
      }

      const scheduleContext: ScheduleContext = {
        ticketCount: scheduleTickets.length,
        jobCategories: Array.from(jobCategories),
        fuelTypes: Array.from(fuelTypes),
        allDTCs: [...new Set(allDTCs)],
        allKeywords: [], // Could extract from notes in future
        dateRange: {
          start: format(startDate, 'yyyy-MM-dd'),
          end: format(endDate, 'yyyy-MM-dd'),
        },
      };

      // Step 4: Fetch training artifacts
      const { data: artifacts, error: artifactsError } = await supabase
        .from('training_artifacts')
        .select('*')
        .eq('is_active', true);

      if (artifactsError) {
        console.error('Failed to fetch training artifacts:', artifactsError);
        throw artifactsError;
      }

      // Step 5: Fetch job mappings for boost
      const { data: jobMappings } = await supabase
        .from('training_artifact_job_map')
        .select('artifact_id, job_id, relevance_weight')
        .eq('is_negative', false);

      const mappings = (jobMappings || []).map(m => ({
        artifactId: m.artifact_id,
        relevanceWeight: m.relevance_weight || 1.0,
      }));

      // Build artifact job ID lookup for adjacency
      const artifactJobIds = new Map<string, string[]>();
      (jobMappings || []).forEach(m => {
        const existing = artifactJobIds.get(m.artifact_id) || [];
        existing.push(m.job_id);
        artifactJobIds.set(m.artifact_id, existing);
      });

      // Step 6: Fetch fitment data for artifacts
      const artifactIds = (artifacts as TrainingArtifact[]).map(a => a.id);
      // BAT-A1: chunk the artifact-id `.in()` so a large catalog (post-
      // content-import 1000+ active artifacts) doesn't push a single
      // unbounded IN(...) clause through PostgREST. The fitment read is
      // a per-Learning-Center-refresh read, so any unbounded shape here
      // hits on every refresh.
      const fitmentRes = await chunkInQuery<
        string,
        {
          artifact_id: string;
          year: number | null;
          make: string | null;
          model: string | null;
          engine: string | null;
          boost: number | null;
        }
      >(artifactIds, (chunk) =>
        supabase
          .from('training_artifact_fitment')
          .select('artifact_id, year, make, model, engine, boost')
          .in('artifact_id', chunk),
      );
      const fitmentData = fitmentRes.data;

      const fitments: FitmentInfo[] = (fitmentData || []).map(f => ({
        artifactId: f.artifact_id,
        year: f.year,
        make: f.make,
        model: f.model,
        engine: f.engine,
        boost: f.boost,
      }));

      // Step 7: Fetch job adjacency map for relevant job IDs
      const uniqueJobIds = [...new Set(allJobIds)];
      // BAT-A1: chunk the source-job-id `.in()` for symmetry with the
      // artifact-id read above. The pre-BAT-A1 `['__none__']` sentinel
      // for the empty case is preserved by `chunkInQuery`'s short-circuit:
      // an empty-keys input returns `{ data: [], error: null }` without
      // invoking the lambda, which is the desired behavior (no rows match
      // a no-keys adjacency lookup; the sentinel was just a workaround for
      // the same outcome via PostgREST).
      const adjacencyRes = await chunkInQuery<
        string,
        { source_job_id: string; related_job_id: string; boost: number | null }
      >(uniqueJobIds, (chunk) =>
        supabase
          .from('job_adjacency_map')
          .select('source_job_id, related_job_id, boost')
          .in('source_job_id', chunk),
      );
      const adjacencyData = adjacencyRes.data;

      const adjacencyMap: AdjacencyInfo[] = (adjacencyData || []).map(a => ({
        sourceJobId: a.source_job_id,
        relatedJobId: a.related_job_id,
        boost: a.boost,
      }));

      // Step 8: Fetch learning profile for style affinity scoring
      const { data: learningProfile } = await supabase
        .from('technician_learning_profile')
        .select('inferred_styles, preferred_styles')
        .eq('technician_profile_id', technicianProfileId)
        .maybeSingle();

      // Step 9: Score artifacts for each urgency category
      const buildContext = (ctxTickets: ScheduleTicket[]): TicketContext => {
        const titles: string[] = [];
        const wfTexts: string[] = [];
        const dtcs: string[] = [];
        const jobIds: string[] = [];
        let fuelType: string | null = null;
        let vehicleYear: number | null = null;
        let vehicleMake: string | null = null;
        let vehicleModel: string | null = null;
        let vehicleEngine: string | null = null;

        for (const ticket of ctxTickets) {
          if (!fuelType) fuelType = inferFuelType(ticket.vehicle?.engine_size);
          if (!vehicleYear && ticket.vehicle?.year) vehicleYear = ticket.vehicle.year;
          if (!vehicleMake && ticket.vehicle?.make) vehicleMake = ticket.vehicle.make;
          if (!vehicleModel && ticket.vehicle?.model) vehicleModel = ticket.vehicle.model;
          if (!vehicleEngine && ticket.vehicle?.engine_size) vehicleEngine = ticket.vehicle.engine_size;
          
          for (const tj of ticket.ticket_jobs || []) {
            if (tj.job?.name) titles.push(tj.job.name);
            if (tj.job?.id) jobIds.push(tj.job.id);
          }

          // Use pre-fetched workflow context
          const wfCtx = workflowContextMap.get(ticket.id);
          if (wfCtx) {
            wfTexts.push(wfCtx.text);
            dtcs.push(...wfCtx.dtcs);
          }
        }

        return {
          vehicleFuelType: fuelType,
          vehicleYear,
          vehicleMake,
          vehicleModel,
          vehicleEngine,
          jobTitles: titles,
          jobIds,
          workflowText: wfTexts.join(' '),
          dtcCodes: [...new Set(dtcs)],
          mode: 'job_specific',
        };
      };

      // Apply style affinity boost (1.0-1.3x) to scored artifacts
      const applyAffinityBoost = (scored: ScoredContextualArtifact[]): ScoredContextualArtifact[] => {
        return scored.map(s => {
          const affinityBoost = getStyleAffinityBoost(
            s.artifact.artifact_type,
            learningProfile?.inferred_styles as Record<LearningStyle, number> | null,
            learningProfile?.preferred_styles as LearningStyle[] | null
          );
          return {
            ...s,
            score: s.score * affinityBoost,
          };
        }).sort((a, b) => b.score - a.score || a.artifact.id.localeCompare(b.artifact.id));
      };

      // Score for "Help Now" (today's tickets)
      const nowContext = buildContext(ticketsByUrgency.now);
      const nowScored = applyAffinityBoost(scoreArtifactsForTicket(
        artifacts as TrainingArtifact[],
        nowContext,
        mappings,
        fitments,
        artifactJobIds,
        adjacencyMap
      ));

      // Score for "Prep Upcoming" (tomorrow + upcoming)
      const upcomingTickets = [...ticketsByUrgency.tomorrow, ...ticketsByUrgency.upcoming];
      const upcomingContext = buildContext(upcomingTickets);
      const upcomingScored = applyAffinityBoost(scoreArtifactsForTicket(
        artifacts as TrainingArtifact[],
        upcomingContext,
        mappings,
        fitments,
        artifactJobIds,
        adjacencyMap
      ));

      // Step 7: Apply caps and deduplicate
      const usedIds = new Set<string>();
      
      const helpNow = nowScored
        .filter(s => s.score > 0)
        .slice(0, 3)
        .map(s => {
          usedIds.add(s.artifact.id);
          return {
            ...s,
            explanation: `For today's work: ${s.explanation}`,
          };
        });

      const prepUpcoming = upcomingScored
        .filter(s => s.score > 0 && !usedIds.has(s.artifact.id))
        .slice(0, 3)
        .map(s => {
          usedIds.add(s.artifact.id);
          return {
            ...s,
            explanation: `Prep for upcoming: ${s.explanation}`,
          };
        });

      // Skill Growth: general foundation content not already shown
      // Date-bound artifacts (live, webinar, hands_on_lab) require at least one future active offering
      const dateBoundTypes = ['live_seminar', 'webinar', 'hands_on_lab'];
      const foundationArtifacts = (artifacts as TrainingArtifact[]).filter(a =>
        !usedIds.has(a.id) &&
        (a as any).skill_depth === 'foundation'
      );

      // Fetch future active offerings for date-bound artifacts
      const dateBoundIds = foundationArtifacts
        .filter(a => a.delivery_format === 'live' || dateBoundTypes.includes(a.artifact_type))
        .map(a => a.id);

      let artifactsWithFutureOfferings = new Set<string>();
      if (dateBoundIds.length > 0) {
        const now = clock.now().toISOString();
        const { data: futureOfferings } = await supabase
          .from('training_offering_instances')
          .select('artifact_id')
          .in('artifact_id', dateBoundIds)
          .eq('is_active', true)
          .gte('start_datetime', now);

        artifactsWithFutureOfferings = new Set(
          (futureOfferings || []).map(o => o.artifact_id)
        );
      }

      const skillGrowth = foundationArtifacts
        .filter(a => {
          const isDateBound = a.delivery_format === 'live' || dateBoundTypes.includes(a.artifact_type);
          if (isDateBound) {
            return artifactsWithFutureOfferings.has(a.id);
          }
          return true; // Evergreen content always passes
        })
        .slice(0, 2)
        .map(artifact => ({
          artifact,
          score: 0,
          matchedKeywords: [],
          matchedDTCs: [],
          explanation: 'Build foundational skills',
          confidenceBadge: 'provisional' as const,
          source: 'general' as const,
        }));

      return {
        recommendations: {
          helpNow,
          prepUpcoming,
          skillGrowth,
        },
        scheduleContext,
        isLoading: false,
      };
    },
    enabled: enabled && !!technicianProfileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
